import type {ModelRef, Ref} from 'vue';

export type ValidationResult = {
  valid: boolean;
  reason: string;  // should be empty, undefined or null if valid is true.
};

export type Validator<T> = (t: T) => ValidationResult;

export type NumericValidator = Validator<number>;

export function makeSimpleNumericBoundsValidator(
  value_name: string,
  lower: number,
  upper: number,
): NumericValidator {
  return (value: number) => {
    if (value >= lower && value <= upper) {
      return {
        valid: true,
        reason: '',
      };
    }

    return {
      valid: false,
      reason: 'unreasonably small, large, or negative ' + value_name,
    };
  };
}

// mess with the element's title attribute to include explanation for
// data invalidity if there is any.
function setAugmentedTitleText(elem: Element) {
  const reason = elem.getAttribute('fin-invalid') ?? '';
  let originalTitle = elem.getAttribute('original-title') ?? '';
  const currentTitle = elem.getAttribute('title') ?? '';

  if (reason) {
    // input is invalid, and we need to share the reason why

    if (!originalTitle) {
      // stash the presumably original title
      originalTitle = currentTitle;
      elem.setAttribute('original-title', originalTitle);
    }

    // write an augmented title in the title attribute, for use as hover
    // text, and maybe other display routes.
    const augmentedTitle = originalTitle + (
      reason ? ('\n\nInvalid: ' + reason) : ''
    );

    elem.setAttribute('title', augmentedTitle);
  } else if (originalTitle) {
    // input is valid, and we need to clean up
    elem.setAttribute('title', originalTitle);
    elem.removeAttribute('original-title');
  }
}

export function makeInComponentValidationWrapper<T>(
  rootElementRef: Ref<Element | undefined>,
  valueRef: ModelRef<T, string, T, T>,
  validator: Validator<T>,
): (evt: Event | undefined) => void {
  return (_evt: Event | undefined) => {
    if (rootElementRef.value === undefined || rootElementRef.value === null) {
      return;
    }

    if (valueRef.value === null || valueRef.value === undefined) {
      return {
        valid: false,
        reason: 'null or undefined',
      };
    }

    const result = validator(valueRef.value as T);

    if (result.valid) {
      (rootElementRef.value as Element).removeAttribute('fin-invalid');
    } else {
      (rootElementRef.value as Element).setAttribute('fin-invalid', result.reason);
    }

    setAugmentedTitleText(rootElementRef.value as Element);
  };
}
